/**
 * AI Feature Toggles Component
 * TASK-335: AI Assistant Configuration & Restrictions
 *
 * Master AI switch and individual feature toggles.
 */

import React from 'react';
import { Power, MessageSquare, Users, FileText, FormInput, FileBarChart } from 'lucide-react';
import type { AISettingsUpdateData } from '../../../types/admin';

interface AIFeatureTogglesProps {
  formData: AISettingsUpdateData;
  onChange: (updates: Partial<AISettingsUpdateData>) => void;
}

const FEATURES = [
  {
    key: 'ai_chat_enabled' as const,
    label: 'AI Chat',
    description: 'Interactive chat assistant for users',
    icon: MessageSquare,
  },
  {
    key: 'ai_meeting_assistant_enabled' as const,
    label: 'Meeting Assistant',
    description: 'Real-time transcription and insights during meetings',
    icon: Users,
  },
  {
    key: 'ai_document_analysis_enabled' as const,
    label: 'Document Analysis',
    description: 'AI-powered document parsing and extraction',
    icon: FileText,
  },
  {
    key: 'ai_form_prefill_enabled' as const,
    label: 'Form Prefill',
    description: 'Smart suggestions for form fields',
    icon: FormInput,
  },
  {
    key: 'ai_brief_generation_enabled' as const,
    label: 'Brief Generation',
    description: 'AI-generated client briefs and summaries',
    icon: FileBarChart,
  },
];

export const AIFeatureToggles: React.FC<AIFeatureTogglesProps> = ({ formData, onChange }) => {
  const isAIEnabled = formData.ai_enabled ?? true;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">AI Features</h3>
        <p className="text-sm text-gray-500 mt-1">
          Control which AI features are available to users in your organization
        </p>
      </div>

      {/* Master Toggle */}
      <div
        className={`
          p-4 rounded-lg border-2 transition-colors
          ${isAIEnabled ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}
        `}
      >
        <label className="flex items-center justify-between cursor-pointer">
          <div className="flex items-center gap-3">
            <div
              className={`
                p-2 rounded-lg
                ${isAIEnabled ? 'bg-green-100' : 'bg-gray-200'}
              `}
            >
              <Power
                size={20}
                className={isAIEnabled ? 'text-green-600' : 'text-gray-400'}
              />
            </div>
            <div>
              <span className="font-medium text-gray-900">AI Assistant</span>
              <p className="text-sm text-gray-500">
                {isAIEnabled ? 'AI features are enabled' : 'All AI features are disabled'}
              </p>
            </div>
          </div>
          <div className="relative">
            <input
              type="checkbox"
              checked={isAIEnabled}
              onChange={(e) => onChange({ ai_enabled: e.target.checked })}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
          </div>
        </label>
      </div>

      {/* Individual Feature Toggles */}
      <div className="space-y-3">
        {FEATURES.map((feature) => {
          const Icon = feature.icon;
          const isEnabled = (formData[feature.key] ?? true) && isAIEnabled;
          const isDisabledByMaster = !isAIEnabled;

          return (
            <label
              key={feature.key}
              className={`
                flex items-center justify-between p-4 rounded-lg border cursor-pointer transition-colors
                ${isDisabledByMaster ? 'opacity-50 cursor-not-allowed bg-gray-50' : ''}
                ${isEnabled && !isDisabledByMaster ? 'border-[#002B45] bg-blue-50' : 'border-gray-200 hover:border-gray-300'}
              `}
            >
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${isEnabled ? 'bg-blue-100' : 'bg-gray-100'}`}>
                  <Icon size={18} className={isEnabled ? 'text-[#002B45]' : 'text-gray-400'} />
                </div>
                <div>
                  <span className="font-medium text-gray-900">{feature.label}</span>
                  <p className="text-sm text-gray-500">{feature.description}</p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={formData[feature.key] ?? true}
                onChange={(e) => onChange({ [feature.key]: e.target.checked })}
                disabled={isDisabledByMaster}
                className="rounded border-gray-300 text-[#002B45] focus:ring-[#F7B500] disabled:opacity-50"
              />
            </label>
          );
        })}
      </div>

      {!isAIEnabled && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-800">
            The master AI toggle is off. Enable it to configure individual features.
          </p>
        </div>
      )}
    </div>
  );
};

export default AIFeatureToggles;
